import React, { useState, useEffect, useCallback } from "react";
import {
    Backdrop, CircularProgress, Box, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, Button, TextField, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import HSTable from "../../components/global/Table";
import Notify from "../../components/global/Notify";
import { getAllPaintSchemes, updatePaintScheme, getPaintSchemeSourcePdf } from "../../services/tables/PaintSchemeTableService";

const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("pt-PT");
};

const PaintSchemeTablePage = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchString, setSearchString] = useState("");
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const [editRow, setEditRow] = useState(null);
    const [newPrice, setNewPrice] = useState("");
    const [saving, setSaving] = useState(false);
    const [loadingSource, setLoadingSource] = useState(false);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAllPaintSchemes(page, rowsPerPage, searchString);
            setRows(res.content);
            setTotalCount(res.totalElements);
        } catch (error) {
            if (!error?.silent) Notify.error(error.message);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, searchString]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const handleEdit = (row) => {
        setEditRow(row);
        setNewPrice(String(row.unitPrice));
    };

    const handleSave = async () => {
        const parsed = parseFloat(newPrice);
        if (isNaN(parsed) || parsed <= 0) { Notify.error("Introduza um valor válido."); return; }
        setSaving(true);
        try {
            await updatePaintScheme(editRow.id, { ...editRow, unitPrice: parsed });
            Notify.success("Preço atualizado.");
            setEditRow(null);
            fetchRows();
        } catch (error) {
            Notify.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleViewSource = async () => {
        const newTab = window.open("", "_blank");
        setLoadingSource(true);
        try {
            const blob = await getPaintSchemeSourcePdf();
            const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
            if (newTab) {
                newTab.location.href = url;
            } else {
                Notify.error("O bloqueador de pop-ups impediu a abertura do PDF.");
            }
        } catch (error) {
            if (newTab) newTab.close();
            Notify.error(error.message);
        } finally {
            setLoadingSource(false);
        }
    };

    const COLUMNS = [
        { field: "code", label: "Código", sortable: true },
        { field: "description", label: "Descrição", sortable: true },
        { field: "application", label: "Aplicação", sortable: true },
        { field: "category", label: "Categoria", sortable: true },
        { field: "unitPrice", label: "Preço (€/m²)", align: "right", sortable: true,
          render: (v) => v?.toFixed(2) },
        { field: "updatedAt", label: "Última Atualização", align:"center",
          render: (v) => formatDate(v) },
        {
            field: "id", label: "Ações", align: "center",
            render: (_, row) => (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row); }}>
                    <EditIcon fontSize="small" />
                </IconButton>
            ),
        },
    ];

    return (
        <Box sx={{ position: "relative", width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={loadingSource ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                    onClick={handleViewSource}
                    disabled={loadingSource}
                >
                    Ver Fonte
                </Button>
            </Box>

            <Box sx={{ position: "relative", boxShadow: 4 }}>
                <HSTable
                    columns={COLUMNS}
                    data={rows}
                    loading={loading}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={totalCount}
                    onPageChange={setPage}
                    onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
                    onSearchChange={(s) => { setSearchString(s); setPage(0); }}
                    searchValue={searchString}
                    enableSearch
                    rowsPerPageOptions={[10, 25, 50]}
                />

                <Backdrop
                    open={loading}
                    sx={{
                        position: "absolute",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        color: "#04642c",
                        zIndex: 1,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress color="inherit" size={40} />
                </Backdrop>
            </Box>

            <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Editar Preço — {editRow?.code}</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
                    <Typography variant="body2">{editRow?.description}</Typography>
                    <TextField
                        label="Preço Unitário (€/m²)"
                        type="number"
                        fullWidth
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                        inputProps={{ min: 0, step: 0.5 }}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditRow(null)} disabled={saving}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? <CircularProgress size={18} /> : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PaintSchemeTablePage;